<template>
  <!-- ========== MAIN CONTENT ========== -->
  <main id="content" role="main">
    <!-- Blog List -->
    <div class="container content-space-3">
      <!-- Header -->
      <div class="w-lg-65 text-center mx-auto mb-5">
        <h1 class="display-5 mb-3">
          <span v-if="tag">Posts tagged with "{{ tag }}"</span>
          <span v-else><span v-html="tenantName"></span> Blog</span>
        </h1>
        <p class="lead">
          Latest updates and insights from <span v-html="tenantName"></span>
        </p>
        <!-- Manage Button for Admins -->
        <div v-if="isAdmin" class="mt-4">
          <a href="/blog/manage" class="btn btn-primary">
            <i class="bi-gear-fill me-2"></i>Manage Blog
          </a>
        </div>
      </div>
      <!-- End Header -->

      <!-- Blog Posts Grid -->
      <div class="position-relative" :style="{ 'min-height': posts.length > 0 || loading ? '400px' : 'auto' }">
        <!-- Loading Overlay -->
        <div v-if="loading" class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" style="background: rgba(255,255,255,0.9); z-index: 10; top: 0; left: 0;">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Posts -->
        <div class="row g-4">
        <div
          v-for="post in posts"
          :key="post.id"
          class="col-md-6 col-lg-4"
        >
          <article class="card h-100 shadow-sm">
            <!-- Featured Image -->
            <a :href="`/blog/article/${post.slug}`" class="text-decoration-none">
              <img
                v-if="post.featuredImage"
                :src="post.featuredImage"
                :alt="post.title"
                class="card-img-top featured-image"
                style="height: 200px; object-fit: cover;"
              >
              <div
                v-else
                class="card-img-top bg-soft-primary d-flex align-items-center justify-content-center"
                style="height: 200px;"
              >
                <i class="bi-file-text" style="font-size: 3rem; color: var(--bs-primary);"></i>
              </div>
            </a>

            <div class="card-body d-flex flex-column">
              <!-- Status Badge -->
              <div v-if="post.status === 'Draft'" class="mb-2">
                <span class="badge bg-warning">Draft</span>
              </div>

              <!-- Title -->
              <h2 class="card-title h5">
                <a :href="`/blog/article/${post.slug}`" class="text-dark text-decoration-none">
                  {{ post.title }}
                </a>
              </h2>

              <!-- Meta Description -->
              <p class="card-text text-muted flex-grow-1">
                {{ post.metaDescription || 'No description available' }}
              </p>

              <!-- Tags -->
              <div v-if="post.tags" class="mb-3">
                <a
                  v-for="postTag in post.tags.split(',').slice(0, 3)"
                  :key="postTag"
                  :href="`/blog/tag/${slugifyTag(postTag)}`"
                  class="badge bg-soft-secondary text-secondary me-1"
                >
                  {{ postTag.trim() }}
                </a>
                <span v-if="post.tags.split(',').length > 3" class="text-muted small">
                  +{{ post.tags.split(',').length - 3 }}
                </span>
              </div>

              <!-- Footer -->
              <div class="d-flex justify-content-between align-items-center mt-auto pt-3 border-top">
                <small class="text-muted">
                  <i class="bi-calendar me-1"></i>
                  {{ formatDate(post.publishedDate || post.createdDate) }}
                </small>
                <a :href="`/blog/article/${post.slug}`" class="link link-sm">
                  Read More <i class="bi-arrow-right"></i>
                </a>
              </div>
            </div>
          </article>
        </div>
        </div>
      </div>
      <!-- End Blog Posts Grid -->

      <!-- Empty State -->
      <div v-if="!loading && posts.length === 0" class="text-center py-10">
        <i class="bi-journal-text" style="font-size: 4rem; color: var(--bs-gray-300);"></i>
        <h3 class="mt-3">No blog posts found</h3>
        <p class="text-muted">
          {{ tag ? `No posts found with tag "${tag}"` : 'No blog posts have been published yet.' }}
        </p>
        <a v-if="tag" href="/blog" class="btn btn-primary mt-3">
          View All Posts
        </a>
      </div>

      <!-- Pagination and Page Size -->
      <div v-if="totalPages > 1 || posts.length > 0" class="mt-5">
        <!-- Page Size Selector -->
        <div class="d-flex justify-content-center mb-3">
          <div class="d-flex align-items-center gap-2">
            <label for="pageSize" class="mb-0 text-muted">Items per page:</label>
            <select
              id="pageSize"
              class="form-select form-select-sm"
              style="width: auto;"
              v-model="pageSize"
              @change="changePageSize"
            >
                <option :value="1">1</option>
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </div>
        </div>

        <!-- Pagination -->
        <nav v-if="totalPages > 1" aria-label="Page navigation">
          <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a
              class="page-link"
              href="#"
              @click.prevent="goToPage(currentPage - 1)"
              :tabindex="currentPage === 1 ? -1 : 0"
            >
              Previous
            </a>
          </li>

          <li
            v-for="page in displayedPages"
            :key="page"
            class="page-item"
            :class="{ active: page === currentPage }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="goToPage(page)"
            >
              {{ page }}
            </a>
          </li>

          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a
              class="page-link"
              href="#"
              @click.prevent="goToPage(currentPage + 1)"
              :tabindex="currentPage === totalPages ? -1 : 0"
            >
              Next
            </a>
          </li>
        </ul>
      </nav>
      </div>
    </div>
    <!-- End Blog List -->
  </main>
  <!-- ========== END MAIN CONTENT ========== -->
</template>

<script>
import axios from '/src/components/NovaAxios.js';

export default {
  name: 'BlogTag',
  data() {
    return {
      posts: window._blogData?.posts || [],
      totalPages: window._blogData?.totalPages || 1,
      currentPage: window._blogData?.currentPage || 1,
      tag: window._blogData?.tag || null,
      tenantName: window._blogData?.tenantName || 'Our',
      pageSize: window._blogData?.pageSize || 10,
      isAdmin: window._blogData?.isAdmin || false,
      loading: false
    };
  },
  computed: {
    displayedPages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    buildPageUrl(page) {
      const params = new URLSearchParams();
      params.set('page', page);
      if (this.pageSize !== 10) {
        params.set('pageSize', this.pageSize);
      }
      if (this.tag) {
        // If on tag page, preserve the tag in URL (slugified)
        return `/blog/tag/${this.slugifyTag(this.tag)}?${params.toString()}`;
      }
      return `?${params.toString()}`;
    },

    slugifyTag(tag) {
      return tag.trim()
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-')     // Replace spaces with hyphens
        .replace(/-+/g, '-')      // Replace multiple hyphens with single
        .replace(/^-|-$/g, '');   // Trim hyphens from ends
    },

    changePageSize() {
      // When page size changes, go back to page 1
      this.pageSize = parseInt(this.pageSize, 10);
      this.goToPage(1);
    },

    async loadPosts(page = 1) {
      // Prevent multiple simultaneous requests
      if (this.loading) return;

      this.loading = true;
      try {
        const params = {
          page: page,
          pageSize: this.pageSize
        };

        if (this.tag) {
          params.tag = this.tag;
        }

        const response = await axios.get('/api/blog/posts', {
          params,
          cache: false
        });

        if (response.data.success) {
          this.posts = response.data.posts;
          this.totalPages = response.data.totalPages;
          this.currentPage = response.data.currentPage;

          // Update URL without refresh
          const url = this.buildPageUrl(page);
          window.history.pushState({ page }, '', url);

          // Smooth scroll to top only if needed
          if (window.scrollY > 300) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        this.loading = false;
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.loadPosts(page);
      }
    }
  },
  mounted() {
    // Handle browser back/forward buttons
    window.addEventListener('popstate', (event) => {
      const page = event.state?.page || 1;
      this.loadPosts(page);
    });
  }
};
</script>

<style scoped>
.py-10 {
  padding-top: 5rem;
  padding-bottom: 5rem;
}

.card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
}

.link {
  color: var(--bs-primary);
  text-decoration: none;
  font-weight: 500;
}

.link:hover {
  color: var(--bs-primary-dark);
  text-decoration: underline;
}

.link-sm {
  font-size: 0.875rem;
}

/* Loading overlay animation */
.position-absolute {
  border-radius: 0.5rem;
}

/* Featured image styling */
.featured-image {
  border-radius: 0.5rem 0.5rem 0 0;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.card:hover .featured-image {
  transform: scale(1.02);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

/* Add subtle border to images */
.card-img-top {
  border-bottom: 1px solid rgba(0,0,0,0.08);
}
</style>
